import * as React from "react"
import {
  type CategoryType,
  DirectionFromOrigin,
  type Flow,
  type HandleNone,
  Interval,
  type IntervalOrigin,
  type PartyRole,
  Reconciliation,
  type SortDirection,
  type SummaryFilterInput,
  type TransactionColumnName,
  type TransactionFilter,
  TransactionFilterRecurrenceStatus,
} from "@digits-graphql/frontend/graphql-bearer"
import { type DigitsLocation } from "@digits-shared/components/Router/DigitsLocation"
import { type RouteParameters } from "@digits-shared/components/Router/DigitsRoute"
import numberHelper from "@digits-shared/helpers/numberHelper"
import objectHelper from "@digits-shared/helpers/objectHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import { useAnalysisCategoryTypes } from "src/frontend/components/OS/Applications/Analysis/AnalysisCategoryTypesContext"
import {
  INITIAL_TRANSACTION_LIST_PAGINATION,
  type TransactionListRequestVariables,
  transactionListRequestVariables,
} from "src/frontend/components/OS/Applications/Shared/Transactions/transactionListRequestVariables"
import {
  TransactionSort,
  type TransactionSortKeys,
} from "src/frontend/components/OS/Applications/Shared/Transactions/TransactionSort"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import routes from "src/frontend/routes"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
 INTERFACES & TYPES
*/

export type TransactionDisplayOptions = Omit<SummaryFilterInput, "viewKey"> & {
  sort: TransactionSort
  otherSideCategoryId?: string
  withoutOtherSideCategoryAncestors?: boolean
  recurrenceStatus?: TransactionFilterRecurrenceStatus
  expandedTransactionId?: string
  expandedCommentId?: string
  recurringThreadId?: string
  reconciliation?: Reconciliation
  handleNone?: HandleNone
  dataSourceId?: string
  sourceAccountId?: string
  naturalFlow?: Flow
  defaultCategoryType?: CategoryType
}

export type PartialTransactionDisplayOptions = Partial<TransactionDisplayOptions>

interface TransactionDisplayOptionsURLParams extends RouteParameters {
  q?: string
  sort?: TransactionSortKeys
  categoryType?: CategoryType
  catId?: string
  withoutCategoryAncestors?: boolean
  otherSideCatId?: string
  withoutOtherSideCategoryAncestors?: boolean
  partyId?: string
  ownerId?: string
  transactionId?: string
  commentId?: string
  recurringThreadId?: string
  recurrence?: TransactionFilterRecurrenceStatus
  min?: number
  max?: number
  reconciliation?: Reconciliation
}

export interface TransactionDisplayOptionsAction {
  partialState: PartialTransactionDisplayOptions
  resetAll?: boolean
  resetMinValue?: boolean
  resetMaxValue?: boolean
}

interface SimpleTransactionListRequestVariables {
  filter: Omit<TransactionFilter, "referenceTransactionId">
  intervalOrigin: IntervalOrigin
  paginationDirection: DirectionFromOrigin
}

export interface TransactionListContextProps {
  defaultDisplayOptions: TransactionDisplayOptions
  displayOptions: TransactionDisplayOptions
  activeDisplayOptionsFilterCount: number
  hasAnyDisplayOptions: boolean
  routeToNewDisplayOptions: (action: TransactionDisplayOptionsAction) => void
  displayOptionsToURLParams: (
    updateOptions: TransactionDisplayOptions
  ) => TransactionDisplayOptionsURLParams
  requestVariables: TransactionListRequestVariables
  nonListRequestVariables: SimpleTransactionListRequestVariables
  skipRequest: boolean
}

export enum TransactionListDisplayOptionsSource {
  Location = "Location",
  Props = "Props",
  // Location and Props arguments merged together. Props will win
  Mixed = "Mixed",
}

interface Props {
  displayOptions?: Partial<TransactionDisplayOptions>
  displayOptionsSource: TransactionListDisplayOptionsSource
  partyRole?: PartyRole
  skipRequest?: boolean
  intervalCount?: number
  intervalOrigin?: IntervalOrigin
}

// These display options don't show in the UI the same as others so we want to ignore them from
// active display options count
const IGNORE_ACTIVE_DISPLAY_OPTIONS: (keyof TransactionDisplayOptions)[] = [
  "expandedTransactionId",
  "expandedCommentId",
  "recurringThreadId",
  "filterTerm",
  "sort",
  "withoutCategoryAncestors",
  "withoutOtherSideCategoryAncestors",
  "categoryType",
  "categoryTypes",
  "partyId",
  "categoryId",
  "handleNone",
  "dataSourceId",
  "sourceAccountId",
]

/*
 HOOK
*/

export const DEFAULT_DISPLAY_OPTIONS: TransactionDisplayOptions = {
  sort: TransactionSort.DEFAULT,
  filterTerm: "",
}

export function useBuildTransactionListContext({
  displayOptions: partialDisplayOptions = {},
  skipRequest,
  partyRole,
  intervalOrigin: overrideIntervalOrigin,
  intervalCount: overrideIntervalCount,
  displayOptionsSource,
}: Props) {
  const { location, params, history, search } = useRouter()
  const categoryTypes = useAnalysisCategoryTypes()

  const displayOptions = React.useMemo(
    (): TransactionDisplayOptions => ({
      ...buildDisplayOptions(displayOptionsSource, location, params, partialDisplayOptions),
      categoryType: undefined,
    }),
    [displayOptionsSource, location, params, partialDisplayOptions]
  )

  const activeDisplayOptionsFilterCount = React.useMemo(
    () =>
      objectHelper
        .keysOf(displayOptions)
        .filter(
          (k: keyof TransactionDisplayOptions) =>
            !!displayOptions[k] && IGNORE_ACTIVE_DISPLAY_OPTIONS.indexOf(k) === -1
        ).length,
    [displayOptions]
  )

  const hasAnyDisplayOptions = React.useMemo(
    () =>
      !!objectHelper
        .keysOf(displayOptions)
        .filter(
          (k) =>
            !!displayOptions[k] && !(k === "sort" && displayOptions[k] === TransactionSort.DEFAULT)
        ).length,
    [displayOptions]
  )

  const displayOptionsToURLParams = React.useCallback(
    (updateOptions: TransactionDisplayOptions): TransactionDisplayOptionsURLParams => {
      const newOptions = { ...updateOptions }

      const newURLParams: TransactionDisplayOptionsURLParams = {}
      if (newOptions.filterTerm) newURLParams.q = newOptions.filterTerm
      if (newOptions.sort && newOptions.sort !== TransactionSort.DEFAULT) {
        newURLParams.sort = newOptions.sort.key
      }
      if (newOptions.categoryId) newURLParams.catId = newOptions.categoryId
      if (newOptions.withoutCategoryAncestors) {
        newURLParams.withoutCategoryAncestors = newOptions.withoutCategoryAncestors
      }
      if (newOptions.otherSideCategoryId) {
        newURLParams.otherSideCatId = newOptions.otherSideCategoryId
      }
      if (newOptions.withoutOtherSideCategoryAncestors) {
        newURLParams.withoutOtherSideCategoryAncestors =
          newOptions.withoutOtherSideCategoryAncestors
      }
      if (newOptions.ownerEmployeeId) newURLParams.ownerId = newOptions.ownerEmployeeId
      if (newOptions.expandedTransactionId) {
        newURLParams.transactionId = newOptions.expandedTransactionId
      }
      if (newOptions.expandedCommentId) newURLParams.commentId = newOptions.expandedCommentId
      if (newOptions.recurringThreadId) {
        newURLParams.recurringThreadId = newOptions.recurringThreadId
      }
      if (newOptions.recurrenceStatus) newURLParams.recurrence = newOptions.recurrenceStatus
      if (newOptions.categoryType) newURLParams.categoryType = newOptions.categoryType

      // TODO: support currencies (multiplier * code)

      if (newOptions.minValue) newURLParams.min = newOptions.minValue?.amount
      if (newOptions.maxValue) newURLParams.max = newOptions.maxValue?.amount

      if (newOptions.reconciliation) newURLParams.reconciliation = newOptions.reconciliation

      return newURLParams
    },
    []
  )

  const routeToNewDisplayOptions = React.useCallback(
    (action: TransactionDisplayOptionsAction) => {
      const updatedOptions = action.resetAll
        ? { ...DEFAULT_DISPLAY_OPTIONS, filterTerm: displayOptions.filterTerm }
        : {
            ...displayOptions,
            ...action.partialState,
            minValue: action.resetMinValue
              ? undefined
              : action.partialState.minValue || displayOptions.minValue,
            maxValue: action.resetMaxValue
              ? undefined
              : action.partialState.maxValue || displayOptions.maxValue,
          }
      history.replace(
        routes[location.name].generateFromCurrentPath(displayOptionsToURLParams(updatedOptions))
      )
    },
    [displayOptions, history, location.name, displayOptionsToURLParams]
  )

  const viewKey = useViewVersion()
  const contextIntervalOrigin = useIntervalOrigin()
  const selectedIntervalOrigin = overrideIntervalOrigin || contextIntervalOrigin

  const intervalCount =
    overrideIntervalCount ||
    parseInt(search.get("intervalCount") ?? "", 10) ||
    selectedIntervalOrigin.intervalCount ||
    1

  const intervalOrigin = React.useMemo(
    () => ({ ...selectedIntervalOrigin, intervalCount: intervalCount }),
    [intervalCount, selectedIntervalOrigin]
  )

  const requestVariables = React.useMemo(
    () =>
      transactionListRequestVariables.variables({
        viewKey,
        partyRole,
        intervalOrigin,
        categoryTypes,
        filter: displayOptions,
      }),
    [viewKey, partyRole, intervalOrigin, displayOptions, categoryTypes]
  )

  return React.useMemo<TransactionListContextProps>(() => {
    const {
      filter: { referenceTransactionId, ...nonBrowserRequestVariables },
      intervalOrigin: requestIntervalOrigin,
      paginationDirection,
    } = requestVariables
    return {
      defaultDisplayOptions: { ...DEFAULT_DISPLAY_OPTIONS },
      displayOptions,
      activeDisplayOptionsFilterCount,
      hasAnyDisplayOptions,
      routeToNewDisplayOptions,
      displayOptionsToURLParams,
      requestVariables,
      nonListRequestVariables: {
        filter: nonBrowserRequestVariables,
        intervalOrigin: requestIntervalOrigin,
        paginationDirection,
      },
      skipRequest: skipRequest || false,
    }
  }, [
    displayOptions,
    activeDisplayOptionsFilterCount,
    hasAnyDisplayOptions,
    routeToNewDisplayOptions,
    displayOptionsToURLParams,
    requestVariables,
    skipRequest,
  ])
}

function buildDisplayOptions(
  displayOptionsSource: TransactionListDisplayOptionsSource,
  location: DigitsLocation,
  params: Record<string, string>,
  displayOptions: Partial<TransactionDisplayOptions>
) {
  switch (displayOptionsSource) {
    case TransactionListDisplayOptionsSource.Location:
      return buildDisplayOptionsFromLocation(location, params)
    case TransactionListDisplayOptionsSource.Props:
      return { ...DEFAULT_DISPLAY_OPTIONS, ...displayOptions }
    case TransactionListDisplayOptionsSource.Mixed:
      return { ...buildDisplayOptionsFromLocation(location, params), ...displayOptions }
  }
}

function buildDisplayOptionsFromLocation(location: DigitsLocation, params: Record<string, string>) {
  const sort =
    TransactionSort.fromKey(location.queryParams.sort as TransactionSortKeys) ||
    TransactionSort.fromColumnAndDirection(
      (location.queryParams.sort as TransactionColumnName) || TransactionSort.DEFAULT.column,
      (location.queryParams.direction as SortDirection) || TransactionSort.DEFAULT.direction
    ) ||
    TransactionSort.DEFAULT

  const min = parseInt(location.queryParams.min || "", 10)
  const max = parseInt(location.queryParams.max || "", 10)

  // TODO: worry about currencies later
  const minValue = isNaN(min) ? undefined : numberHelper.buildMonetaryAmountFromUnmultiplied(min)
  const maxValue = isNaN(max) ? undefined : numberHelper.buildMonetaryAmountFromUnmultiplied(max)

  return {
    filterTerm: location.queryParams.q || "",
    sort,
    categoryId: location.queryParams.categoryId || params?.categoryId || location.queryParams.catId,
    withoutCategoryAncestors:
      location.queryParams.categoryWithoutAncestors === "true" ? true : undefined,
    otherSideCategoryId:
      location.queryParams.otherSideCategoryId || location.queryParams.otherSideCatId,
    withoutOtherSideCategoryAncestors:
      location.queryParams.otherSideCategoryWithoutAncestors === "true" ? true : undefined,
    partyId: location.queryParams.partyId || params?.partyId,
    ownerEmployeeId: location.queryParams.ownerEmployeeId || location.queryParams.ownerId,
    recurrenceStatus:
      TransactionFilterRecurrenceStatus[
        location.queryParams.recurrence as TransactionFilterRecurrenceStatus
      ],
    expandedTransactionId: location.queryParams.transactionId,
    expandedCommentId: location.queryParams.commentId,
    recurringThreadId: location.queryParams.recurringThreadId,
    minValue,
    maxValue,
    reconciliation: Reconciliation[location.queryParams.reconciliation as Reconciliation],
  }
}

export const TransactionListContext = React.createContext<TransactionListContextProps>({
  defaultDisplayOptions: DEFAULT_DISPLAY_OPTIONS,
  displayOptions: DEFAULT_DISPLAY_OPTIONS,
  activeDisplayOptionsFilterCount: 0,
  hasAnyDisplayOptions: false,
  routeToNewDisplayOptions: () => {},
  displayOptionsToURLParams: () => ({}),
  requestVariables: {
    pagination: { ...INITIAL_TRANSACTION_LIST_PAGINATION },
    paginationDirection: DirectionFromOrigin.Past,
    sortColumnName: TransactionSort.DEFAULT.column,
    sortDirection: TransactionSort.DEFAULT.direction,
    filter: {
      viewKey: {
        legalEntityId: "",
      },
    },
    intervalOrigin: {
      interval: Interval.Month,
      year: 0,
      index: 0,
    },
  },
  nonListRequestVariables: {
    filter: {
      viewKey: {
        legalEntityId: "",
      },
    },
    intervalOrigin: {
      interval: Interval.Month,
      year: 0,
      index: 0,
    },
    paginationDirection: DirectionFromOrigin.Past,
  },
  skipRequest: false,
})

export function useTransactionListContext(): TransactionListContextProps {
  return React.useContext(TransactionListContext)
}
